/**
 * Booking Redux Slice
 * Manages slot booking state and actions
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as bookingApi from '../../services/bookingApi';

// Async thunk: Book a slot
export const createBooking = createAsyncThunk(
  'booking/create',
  async ({ slotId, userNotes }, { rejectWithValue }) => {
    try {
      const response = await bookingApi.bookSlot(slotId, userNotes);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk: Fetch user's bookings
export const fetchMyBookings = createAsyncThunk(
  'booking/fetchMy',
  async (_, { rejectWithValue }) => {
    try {
      const response = await bookingApi.getMyBookings();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk: Fetch astrologer's bookings
export const fetchAstrologerBookings = createAsyncThunk(
  'booking/fetchAstrologer',
  async (_, { rejectWithValue }) => {
    try {
      const response = await bookingApi.getAstrologerBookings();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk: Cancel a booking
export const cancelBooking = createAsyncThunk(
  'booking/cancel',
  async ({ bookingId }, { rejectWithValue }) => {
    try {
      const response = await bookingApi.cancelBooking(bookingId);
      return { response, bookingId };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk: Complete a booking
export const completeBooking = createAsyncThunk(
  'booking/complete',
  async ({ bookingId }, { rejectWithValue }) => {
    try {
      const response = await bookingApi.completeBooking(bookingId);
      return { response, bookingId };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk: Get booking details
export const fetchBookingDetails = createAsyncThunk(
  'booking/fetchDetails',
  async ({ bookingId }, { rejectWithValue }) => {
    try {
      const response = await bookingApi.getBookingDetails(bookingId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const bookingSlice = createSlice({
  name: 'booking',
  initialState: {
    myBookings: [],
    astrologerBookings: [],
    selectedBooking: null,
    selectedSlot: null,
    bookingDialogOpen: false,
    userNotes: '',
    loading: false,
    error: null,
    bookingStatus: 'idle', // 'idle' | 'loading' | 'success' | 'error'
  },
  reducers: {
    setSelectedSlot: (state, action) => {
      console.log('[Redux] Selected slot:', action.payload);
      state.selectedSlot = action.payload;
    },
    openBookingDialog: (state, action) => {
      console.log('[Redux] Opening booking dialog for slot:', action.payload);
      state.selectedSlot = action.payload;
      state.bookingDialogOpen = true;
      state.userNotes = '';
    },
    closeBookingDialog: (state) => {
      console.log('[Redux] Closing booking dialog');
      state.bookingDialogOpen = false;
      state.selectedSlot = null;
      state.userNotes = '';
      state.error = null;
    },
    setUserNotes: (state, action) => {
      state.userNotes = action.payload;
    },
    resetBookingStatus: (state) => {
      state.bookingStatus = 'idle';
      state.error = null;
    },
    clearSelectedSlot: (state) => {
      state.selectedSlot = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create booking
      .addCase(createBooking.pending, (state) => {
        state.bookingStatus = 'loading';
        state.error = null;
      })
      .addCase(createBooking.fulfilled, (state, action) => {
        state.bookingStatus = 'success';
        state.bookingDialogOpen = false;
        state.selectedSlot = null;
        state.userNotes = '';
        console.log('[Redux] Booking created successfully:', action.payload);
      })
      .addCase(createBooking.rejected, (state, action) => {
        state.bookingStatus = 'error';
        state.error = action.payload;
        console.error('[Redux] Create booking failed:', action.payload);
      })

      // Fetch my bookings
      .addCase(fetchMyBookings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyBookings.fulfilled, (state, action) => {
        state.loading = false;
        state.myBookings = action.payload.bookings || [];
        console.log(`[Redux] Loaded ${action.payload.bookings?.length || 0} user bookings`);
      })
      .addCase(fetchMyBookings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        console.error('[Redux] Fetch my bookings failed:', action.payload);
      })

      // Fetch astrologer bookings
      .addCase(fetchAstrologerBookings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAstrologerBookings.fulfilled, (state, action) => {
        state.loading = false;
        state.astrologerBookings = action.payload.bookings || [];
        console.log(`[Redux] Loaded ${action.payload.bookings?.length || 0} astrologer bookings`);
      })
      .addCase(fetchAstrologerBookings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        console.error('[Redux] Fetch astrologer bookings failed:', action.payload);
      })

      // Cancel booking
      .addCase(cancelBooking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelBooking.fulfilled, (state, action) => {
        state.loading = false;
        // Update booking status in the list
        const updateBookingStatus = (bookings) => {
          const booking = bookings.find(b => b.id === action.payload.bookingId);
          if (booking) {
            booking.status = 'cancelled';
          }
        };
        updateBookingStatus(state.myBookings);
        updateBookingStatus(state.astrologerBookings);
        console.log('[Redux] Booking cancelled successfully');
      })
      .addCase(cancelBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        console.error('[Redux] Cancel booking failed:', action.payload);
      })

      // Complete booking
      .addCase(completeBooking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(completeBooking.fulfilled, (state, action) => {
        state.loading = false;
        // Update booking status in the list
        const updateBookingStatus = (bookings) => {
          const booking = bookings.find(b => b.id === action.payload.bookingId);
          if (booking) {
            booking.status = 'completed';
          }
        };
        updateBookingStatus(state.myBookings);
        updateBookingStatus(state.astrologerBookings);
        console.log('[Redux] Booking completed successfully');
      })
      .addCase(completeBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        console.error('[Redux] Complete booking failed:', action.payload);
      })

      // Fetch booking details
      .addCase(fetchBookingDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBookingDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedBooking = action.payload;
        console.log('[Redux] Booking details loaded');
      })
      .addCase(fetchBookingDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        console.error('[Redux] Fetch booking details failed:', action.payload);
      });
  },
});

export const {
  setSelectedSlot,
  openBookingDialog,
  closeBookingDialog,
  setUserNotes,
  resetBookingStatus,
  clearSelectedSlot,
} = bookingSlice.actions;

export default bookingSlice.reducer;
